import { Link, useRouterState } from '@tanstack/react-router';
import { LayoutDashboard, Users, User, Bell } from 'lucide-react';
import { cn, CompassLogo } from '@jeraisonne/ui';
import { NavBadge } from '@/components/nav-badge';

const LEFT_NAV = [
  { id: 'dashboard', label: 'Accueil', icon: LayoutDashboard, to: '/student/dashboard' },
  { id: 'news', label: 'Feed', icon: Bell, to: '/student/news' },
] as const;

const RIGHT_NAV = [
  { id: 'contacts', label: 'Mon réseau', icon: Users, to: '/student/contacts' },
  { id: 'profile', label: 'Profil', icon: User, to: '/profile' },
] as const;

const REVISE_NAV = { id: 'revise', label: 'JeRaisonne', to: '/student/revise' } as const;

const HIDDEN_ON = ['/student/warmup'];

/** `feedBadge` = nouveautés du Feed (0 = pas de pastille). */
export function StudentBottomNav({ feedBadge = 0 }: { feedBadge?: number }) {
  const location = useRouterState({ select: (s) => s.location.pathname });
  const reviseActive = location.startsWith(REVISE_NAV.to);

  if (HIDDEN_ON.some((path) => location.startsWith(path))) return null;

  return (
    <nav className="border-ink-100 native:pb-[min(env(safe-area-inset-bottom),2.125rem)] native:h-[calc(3.5rem+min(env(safe-area-inset-bottom),2.125rem))] ocean:bg-[#061433] ocean:shadow-[0_-4px_20px_rgba(0,0,0,0.35)] fixed inset-x-0 bottom-0 z-10 flex h-[calc(3.5rem+env(safe-area-inset-bottom))] items-end border-t bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(5,102,242,0.08)] md:hidden">
      {LEFT_NAV.map(({ id, label, icon: Icon, to }) => {
        const active = location.startsWith(to);
        return (
          <Link
            key={id}
            to={to}
            className={cn(
              'font-display flex flex-1 flex-col items-center gap-0.5 pb-2 pt-2.5 text-[11px] font-semibold transition-colors',
              active ? 'text-primary-600' : 'text-ink-400',
            )}
          >
            <span className="relative">
              <Icon className={cn('h-5 w-5', active ? 'text-primary-600' : 'text-ink-400')} />
              {id === 'news' && <NavBadge count={feedBadge} variant="icon" />}
            </span>
            <span className="truncate">{label}</span>
          </Link>
        );
      })}

      <div className="relative flex flex-1 flex-col items-center pb-2">
        <Link
          to={REVISE_NAV.to}
          className={cn(
            'ocean:ring-[#061433] absolute -top-5 flex h-14 w-14 flex-col items-center justify-center rounded-full bg-[radial-gradient(circle_at_50%_35%,#2aa3e6,#0a78c8)] shadow-[0_8px_20px_rgba(10,120,200,0.55)] ring-4 ring-white transition-all active:scale-95',
            reviseActive ? 'shadow-[0_8px_24px_rgba(10,120,200,0.65)]' : 'hover:brightness-110',
          )}
          aria-label="JeRaisonne"
        >
          <CompassLogo size="lg" animated={reviseActive} showWordmark={false} />
        </Link>
        <span
          className={cn(
            'font-display mt-10 text-[11px] font-semibold',
            reviseActive ? 'text-primary-600' : 'text-ink-400',
          )}
        >
          JeRaisonne
        </span>
      </div>

      {RIGHT_NAV.map(({ id, label, icon: Icon, to }) => {
        const active = location.startsWith(to);
        return (
          <Link
            key={id}
            to={to}
            className={cn(
              'font-display flex flex-1 flex-col items-center gap-0.5 pb-2 pt-2.5 text-[11px] font-semibold transition-colors',
              active ? 'text-primary-600' : 'text-ink-400',
            )}
          >
            <Icon className={cn('h-5 w-5', active ? 'text-primary-600' : 'text-ink-400')} />
            <span className="truncate">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
